import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Input,
  Select,
  message,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Image,
  Tooltip,
  Divider,
  Empty,
  Collapse,
  Button,
  Spin
} from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FolderOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';

const { Text, Title } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

// Helper function to extract video thumbnail
const getVideoThumbnail = (url) => {
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (youtubeMatch) {
    return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
  }
  
  return null;
};

// Helper function to get file icon based on extension
const getFileIcon = (filename, resourceType) => {
  if (resourceType === 'video_link') {
    return <VideoCameraOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
  }
  if (resourceType === 'file_link') {
    return <LinkOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
  }
  if (resourceType === 'file_upload') {
    return <FileTextOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
  }
  return <FileTextOutlined style={{ fontSize: '24px', color: '#666' }} />;
};

const ResourceView = () => {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterSubject, setFilterSubject] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [viewMode, setViewMode] = useState('category'); // 'category' or 'all'

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchResources();
    fetchCategories();
    fetchSubjects();
  }, []);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      message.error(t('Failed to fetch resources'));
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/resources/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/resources/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Filter resources based on search and filters
  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchText || 
      resource.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesSubject = !filterSubject || resource.subject_id === filterSubject;
    const matchesType = !filterType || resource.resource_type === filterType;

    return matchesSearch && matchesSubject && matchesType;
  });

  // Group resources by category
  const resourcesByCategory = categories.map(category => ({
    ...category,
    resources: filteredResources.filter(resource => resource.category_id === category.id)
  })).filter(category => category.resources.length > 0);

  // Resource Card Component
  const ResourceCard = ({ resource }) => (
    <Card
      hoverable
      style={{ 
        height: '100%',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease'
      }}
      bodyStyle={{ padding: 0 }}
      cover={
        resource.resource_type === 'video_link' && resource.link && getVideoThumbnail(resource.link) ? (
          <div style={{ position: 'relative', height: 160 }}>
            <Image
              width="100%"
              height={160}
              src={getVideoThumbnail(resource.link)}
              style={{ objectFit: 'cover' }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6U"
            />
            <div style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12
            }}>
              <VideoCameraOutlined /> Video
            </div>
          </div>
        ) : (
          <div style={{ 
            height: 160, 
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            {getFileIcon(resource.file_path, resource.resource_type)}
            <Text type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
              {resource.resource_type === 'file_link' ? 'External Link' :
               resource.resource_type === 'file_upload' ? 'PDF Document' : 'Resource'}
            </Text>
          </div>
        )
      }
      actions={[
        resource.resource_type === 'video_link' || resource.resource_type === 'file_link' ? (
          <Tooltip title={t('Open Link')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => window.open(resource.link, '_blank')}
            >
              {t('View')}
            </Button>
          </Tooltip>
        ) : resource.file_path ? (
          <Tooltip title={t('Download')}>
      <Button
        type="text"
        icon={<DownloadOutlined />}
        onClick={async () => {
          try {
            const filename = resource.file_path.split('/').pop();
            if (!filename) {
              message.error(t('Invalid resource path'));
              return;
            }

            const downloadUrl = `${API_URL}/resources/download/${encodeURIComponent(filename)}`;
            const response = await fetch(downloadUrl);

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Force browser download in new tab
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
          } catch (err) {
            console.error('❌ Error downloading resource:', err);
            message.error(t('Failed to download resource'));
          }
        }}
      >
        {t('Download')}
      </Button>
    </Tooltip>
        ) : (
          <Button type="text" disabled>
            {t('No File')}
          </Button>
        )
      ]}
    >
      <div style={{ padding: 16 }}>
        <Title 
          level={5} 
          style={{ 
            margin: 0, 
            marginBottom: 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {resource.title}
        </Title>
        
        <Text 
          type="secondary" 
          style={{ 
            fontSize: 12,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            minHeight: 34
          }}
        >
          {resource.description || 'No description provided'}
        </Text>
        
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Tag color="green" size="small">
            {resource.subject_name || 'Unknown'}
          </Tag>
          <Tag 
            color={
              resource.resource_type === 'video_link' ? 'red' :
              resource.resource_type === 'file_link' ? 'blue' : 'cyan'
            }
            size="small"
          >
            {resource.resource_type === 'video_link' ? 'Video' :
             resource.resource_type === 'file_link' ? 'Link' : 'PDF'}
          </Tag>
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div style={{ margin: '70px 20px 20px 90px', padding: '20px' }}>
        <Card style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 24 }}>
            <Title level={2}>{t('Resource Library')}</Title>
            <Text type="secondary">
              {t('Browse and access educational resources organized by categories')}
            </Text>
            
            {/* Filters and Search */}
            <Row gutter={[16, 16]} style={{ marginTop: 20, marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder={t('Search resources...')}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  size="large"
                />
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('Subject')}
                  value={filterSubject}
                  onChange={setFilterSubject}
                  allowClear
                  style={{ width: '100%' }}
                  size="large"
                >
                  {subjects.map(subj => (
                    <Option key={subj.id} value={subj.id}>{subj.name}</Option>
                  ))}
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('Type')}
                  value={filterType}
                  onChange={setFilterType}
                  allowClear
                  style={{ width: '100%' }}
                  size="large"
                >
                  <Option value="video_link">Video</Option>
                  <Option value="file_link">Link</Option>
                  <Option value="file_upload">PDF</Option>
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('View Mode')}
                  value={viewMode}
                  onChange={setViewMode}
                  style={{ width: '100%' }}
                  size="large"
                >
                  <Option value="category">By Category</Option>
                  <Option value="all">All Resources</Option>
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchResources}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {t('Refresh')}
                </Button>
              </Col>
            </Row>

            <Divider />
            
            {/* Results Summary */}
            <Space style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Showing {filteredResources.length} of {resources.length} resources
              </Text>
              {viewMode === 'category' && (
                <Text type="secondary">
                  across {resourcesByCategory.length} categories
                </Text>
              )}
            </Space>
          </div>
        </Card>

        <Spin spinning={loading}>
          {viewMode === 'category' ? (
            // Category View
            <>
              {resourcesByCategory.length > 0 ? (
                <Collapse 
                  defaultActiveKey={resourcesByCategory.map(cat => cat.id.toString())}
                  ghost
                  size="large"
                  style={{ backgroundColor: 'transparent' }}
                >
                  {resourcesByCategory.map(category => (
                    <Panel
                      header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <FolderOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                          <Title level={4} style={{ margin: 0 }}>
                            {category.name}
                          </Title>
                          <Tag color="blue">{category.resources.length} resources</Tag>
                        </div>
                      }
                      key={category.id}
                      style={{ 
                        marginBottom: 16,
                        backgroundColor: '#fafafa',
                        borderRadius: 12,
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <Row gutter={[16, 16]}>
                        {category.resources.map(resource => (
                          <Col xs={24} sm={12} md={8} lg={6} key={resource.id}>
                            <ResourceCard resource={resource} />
                          </Col>
                        ))}
                      </Row>
                    </Panel>
                  ))}
                </Collapse>
              ) : (
                <Card>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <Title level={4} type="secondary">
                          {searchText || filterSubject || filterType 
                            ? t('No resources match your filters') 
                            : t('No resources available')
                          }
                        </Title>
                        <Text type="secondary">
                          {searchText || filterSubject || filterType
                            ? t('Try adjusting your search criteria')
                            : t('Resources will appear here when they are added')
                          }
                        </Text>
                      </div>
                    }
                  />
                </Card>
              )}
            </>
          ) : (
            // All Resources View
            <Card>
              {filteredResources.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {filteredResources.map(resource => (
                    <Col xs={24} sm={12} md={8} lg={6} key={resource.id}>
                      <ResourceCard resource={resource} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <Title level={4} type="secondary">
                        {searchText || filterSubject || filterType 
                          ? t('No resources match your filters') 
                          : t('No resources available')
                        }
                      </Title>
                      <Text type="secondary">
                        {searchText || filterSubject || filterType
                          ? t('Try adjusting your search criteria')
                          : t('Resources will appear here when they are added')
                        }
                      </Text>
                    </div>
                  }
                />
              )}
            </Card>
          )}
        </Spin>
      </div>

      <style jsx>{`
        .ant-collapse-content-box {
          padding: 20px !important;
        }
        .ant-collapse-header {
          padding: 16px 20px !important;
        }
        .ant-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
        }
        @media (max-width: 768px) {
          .ant-col {
            margin-bottom: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default withRoleAccess(ResourceView, ['teacher']);